import { AnimatePresence, motion } from "motion/react";
import { primeObservatoryAudio } from "../experience/ObservatoryAudioRuntime";
import { useEffect, useMemo, useState } from "react";
import { getCatalog } from "../i18n";
import { useMotionPolicy } from "../lib/hooks/useMotionPolicy";
import { usePresentedSnapshot } from "../presentation/hooks";
import { useNexusStore } from "../store/nexusStore";
import { panelSpring, softSpring, uiSpring } from "../lib/motion";
import { LocaleSwitch } from "./LocaleSwitch";
function useClock() {
  const [value, setValue] = useState(new Date());
  useEffect(() => {
    let interval = 0;
    const alignToMinute = window.setTimeout(
      () => {
        setValue(new Date());
        interval = window.setInterval(() => setValue(new Date()), 60_000);
      },
      60_000 - (Date.now() % 60_000),
    );
    return () => {
      window.clearTimeout(alignToMinute);
      window.clearInterval(interval);
    };
  }, []);
  return value;
}

export function TopBar() {
  const snapshot = usePresentedSnapshot();
  const setCommandOpen = useNexusStore((state) => state.setCommandOpen);
  const locale = useNexusStore((state) => state.locale);
  const soundEnabled = useNexusStore((state) => state.soundEnabled);
  const toggleSound = useNexusStore((state) => state.toggleSound);
  const accessibleMode = useNexusStore((state) => state.accessibleMode);
  const toggleAccessibleMode = useNexusStore((state) => state.toggleAccessibleMode);
  const motionPolicy = useMotionPolicy();
  const clock = useClock();
  const ui = getCatalog(locale).ui;
  const focus = useNexusStore((state) => state.focus);
  const heroVisible = useNexusStore((state) => state.heroVisible);
  const demoTourActive = useNexusStore((state) => state.demoTourActive);
  const setDemoTourActive = useNexusStore((state) => state.setDemoTourActive);
  const focusCosmos = useNexusStore((state) => state.focusCosmos);
  const online = useMemo(
    () => snapshot?.nodes.filter((node) => node.kind === "system" && node.status !== "offline").length ?? 0,
    [snapshot],
  );
  const systems = snapshot?.nodes.filter((node) => node.kind === "system").length ?? 0;
  const timeLabel = clock.toLocaleTimeString(locale === "en" ? "en-GB" : locale === "pl" ? "pl-PL" : "de-DE", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
  });
  const locationModel = useMemo(() => {
    if (!snapshot || focus.kind === "cosmos") {
      return { eyebrow: ui.nowLabel, label: ui.overviewLabel };
    }

    if (focus.kind === "node") {
      const node = snapshot.nodes.find((item) => item.id === focus.nodeId);
      return { eyebrow: node?.eyebrow ?? ui.nowLabel, label: node?.label ?? ui.overviewLabel };
    }

    const orbital = snapshot.orbitalNodes.find((item) => item.id === focus.orbitalId);
    return { eyebrow: orbital?.eyebrow ?? ui.nowLabel, label: orbital?.label ?? ui.overviewLabel };
  }, [focus, snapshot, ui.nowLabel, ui.overviewLabel]);

  return (
    <motion.header
      className="topbar"
      initial={motionPolicy.reduced ? false : { opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={motionPolicy.reduced ? { duration: 0 } : { ...panelSpring, delay: 0.18 }}
    >
      <div className="topbar-shell">
        <div className="brand-block">
          <div className="brand-mark" aria-hidden="true">
            <span />
          </div>
          <div>
            <strong>K·NEXUS</strong>
            <span>{ui.brandTagline}</span>
          </div>
        </div>

        <motion.button
          whileHover={{ y: -1 }}
          whileTap={{ scale: 0.992 }}
          transition={uiSpring}
          className="context-switcher"
          onClick={() => {
            if (demoTourActive) setDemoTourActive(false);
            setCommandOpen(true);
          }}
          aria-label={ui.commandPaletteLabel}
        >
          <span className="context-switcher__stack">
            <AnimatePresence mode="popLayout" initial={false}>
              <motion.span
                key={`eyebrow-${locationModel.eyebrow}`}
                className="context-switcher__eyebrow"
                initial={{ opacity: 0, y: -4, filter: "blur(3px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: 4, filter: "blur(2px)" }}
                transition={softSpring}
              >
                {locationModel.eyebrow}
              </motion.span>
              <motion.span
                key={`value-${locationModel.label}`}
                className="context-switcher__value"
                initial={{ opacity: 0, y: -4, filter: "blur(3px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: 4, filter: "blur(2px)" }}
                transition={softSpring}
              >
                {locationModel.label}
              </motion.span>
            </AnimatePresence>
          </span>
          <span className="context-switcher__chevron" aria-hidden="true">
            <svg viewBox="0 0 16 16" fill="none">
              <path
                d="M4.25 6.25 8 10l3.75-3.75"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </motion.button>

        <div className="topbar__instruments">
          <div className="instrument instrument--pill" aria-label={`${online} / ${systems} ${ui.systemsNominal}`}>
            <span className="signal-dot" aria-hidden="true" />
            <b>
              {online.toString().padStart(2, "0")}/{systems.toString().padStart(2, "0")}
            </b>
            <i className="instrument__separator" aria-hidden="true">
              ·
            </i>
            <small>{ui.systemsNominal}</small>
          </div>
          <div className="instrument instrument--clock-pill" aria-label={timeLabel}>
            <b>{timeLabel}</b>
          </div>
          <button
            className={`sound-trigger${soundEnabled ? " is-active" : ""}`}
            onClick={() => {
              primeObservatoryAudio();
              toggleSound();
            }}
            aria-pressed={soundEnabled}
            aria-label={soundEnabled ? "Disable observatory sound" : "Enable observatory sound"}
          >
            <span aria-hidden="true">{soundEnabled ? "◉" : "○"}</span>
            <small>{ui.sound}</small>
          </button>
          <button
            className={`accessibility-trigger${accessibleMode ? " is-active" : ""}`}
            onClick={toggleAccessibleMode}
            aria-pressed={accessibleMode}
            aria-label={accessibleMode ? ui.accessibleObservatoryOn : ui.accessibleObservatoryOff}
          >
            <span aria-hidden="true">{accessibleMode ? "◈" : "◇"}</span>
            <small>{ui.access}</small>
          </button>
          <LocaleSwitch />
          <button
            className={`command-trigger${heroVisible && !demoTourActive ? " command-trigger--muted" : " command-trigger--cta"}${demoTourActive ? " is-demo-tour-active" : ""}`}
            onClick={() => {
              if (demoTourActive) {
                setDemoTourActive(false);
                focusCosmos();
                return;
              }
              setCommandOpen(true);
            }}
            aria-label={demoTourActive ? ui.exitDemoTour : ui.getStarted}
          >
            <span>{demoTourActive ? ui.exitDemoTour : ui.getStarted}</span>
            <kbd>{demoTourActive ? "■" : "→"}</kbd>
          </button>
        </div>
      </div>
    </motion.header>
  );
}
